import { fetcher } from '@commenda/utils';
import useSWR from 'swr';

import {
  IIndirectTaxFilingV2AggregateParams,
  IIndirectTaxFilingV2AggregateResponse,
} from './types';

/**
 * POST /api/v1/new-sales-tax/filings-v2/aggregate
 *
 * Returns bucketed summary totals for the Indirect Tax Filings dashboard
 * cards — sums across every filing matching the filter set so the cards
 * stay correct under pagination (the list endpoint cursor-paginates;
 * summing the visible page lies for any corporation with more than one
 * page).
 *
 * Pass `null` to opt out: the hook returns `{ aggregate: null, ... }` with
 * no network call. The All-regions tab uses this because the backend
 * refuses cross-region aggregation.
 */
export function useIndirectTaxFilingsAggregate(
  params: IIndirectTaxFilingV2AggregateParams | null
) {
  // Explicit, order-independent cache key. A field-name-prefixed join keeps
  // the cache stable across param-object reorderings — JSON.stringify on
  // unsorted objects can silently invalidate on identical filter sets.
  const key = params ? buildAggregateKey(params) : null;

  const { data, error, isLoading, mutate } =
    useSWR<IIndirectTaxFilingV2AggregateResponse>(
      key,
      async () => {
        if (!params) {
          // SWR only invokes the fetcher when key is non-null. Surface a
          // typed Error rather than returning a placeholder — SWR routes
          // it onto the `error` channel and the caller can react.
          throw new Error(
            'useIndirectTaxFilingsAggregate fetcher invoked without params'
          );
        }
        const response = await fetcher('new-sales-tax/filings-v2/aggregate', {
          method: 'POST',
          body: params,
        });
        const json = await response.json();
        if (!response.ok) {
          // Backend returns an RFC9457 body shaped { error: { title,
          // detail: { description }, ... } } — there's no top-level
          // `message`, so read detail.description first (the precise reason,
          // e.g. the multi-region jurisdiction), then title, matching the
          // convention in createIndirectTaxRegistration. Falling back to a
          // generic string only when the body is unrecognizable.
          const errorBody = json as
            | { error?: { title?: string; detail?: { description?: string } } }
            | null
            | undefined;
          const message =
            errorBody?.error?.detail?.description ??
            errorBody?.error?.title ??
            'Failed to aggregate filings.';
          throw new Error(message);
        }
        return json as IIndirectTaxFilingV2AggregateResponse;
      },
      // keepPreviousData holds the prior cards across filter changes so they
      // don't flash while the next aggregate loads. shouldRetryOnError:false
      // because aggregate failures are typed 400s (e.g. multi-region) that
      // won't succeed on retry — retrying only fires duplicate error toasts.
      { keepPreviousData: true, shouldRetryOnError: false }
    );

  return {
    // On error, return null rather than the kept-previous `data`: a failed
    // region must drop its cards to dashes, not show the last region's
    // numbers. keepPreviousData still covers the in-flight (no-error) case.
    aggregate: error ? null : (data ?? null),
    isLoading,
    error,
    mutate,
  };
}

function buildAggregateKey(params: IIndirectTaxFilingV2AggregateParams): string {
  return [
    'new-sales-tax/filings-v2/aggregate',
    `corporationId=${params.corporationId}`,
    `region=${params.region}`,
    `registrationTaxTypeId=${params.registrationTaxTypeId ?? ''}`,
    `jurisdictionIds=${(params.jurisdictionIds ?? []).slice().sort().join(',')}`,
    `filingStatuses=${(params.filingStatuses ?? []).slice().sort().join(',')}`,
    `filingFrequencies=${(params.filingFrequencies ?? []).slice().sort().join(',')}`,
    `fromDueDate=${params.fromDueDate ?? ''}`,
    `toDueDate=${params.toDueDate ?? ''}`,
  ].join('::');
}
